/**
 * useEditingState — S6.2 F1 editing session hook.
 *
 * Owns the reconstructed editing state for one comparison and the four
 * mutations the editor exposes (set-text / insert / delete / undo). Every
 * mutation is server-authoritative: the backend re-runs the reconstruction
 * engine and returns the new state, which we swap in wholesale (no optimistic
 * recompute on the client — the redline/clean derivation lives in one place).
 *
 * Activation (entering editing) materializes the working tree, which freezes
 * the comparison (R1, ADR-112). For an editing role we POST (materialize);
 * for a viewer we GET (read-only preview) and surface the "nobody has entered
 * editing yet" case as `not-materialized` rather than an error.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../lib/transport';
import {
  veridocApi,
  type AppendEditRequest,
  type EditingRun,
  type EditingState,
} from '../../lib/veridoc-api';

export type EditingStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'not-materialized'
  | 'error';

export interface UseEditingStateArgs {
  comparisonId: string | undefined;
  /** Only activate when the editing view is the active view mode. */
  enabled: boolean;
  /** lawyer/reviewer materialize on entry; viewer reads only. */
  canEdit: boolean;
  onError: (message: string) => void;
}

export interface UseEditingStateResult {
  state: EditingState | null;
  status: EditingStatus;
  /** A mutation (or activation) is in flight. */
  busy: boolean;
  canUndo: boolean;
  /** Replace a node's text (plain-text rewrite — D-F1-2=A). */
  setText: (nodeId: string, text: string) => Promise<void>;
  /** Insert a new node at a clean-children index. */
  insertNode: (index: number, text: string, type?: string) => Promise<void>;
  /** Delete a node (struck in the redline preview). */
  deleteNode: (nodeId: string) => Promise<void>;
  /** Undo the most recent edit. */
  undo: () => Promise<void>;
}

const NOT_MATERIALIZED = 'VD_COMPARISON_NOT_MATERIALIZED';

export function useEditingState({
  comparisonId,
  enabled,
  canEdit,
  onError,
}: UseEditingStateArgs): UseEditingStateResult {
  const [state, setState] = useState<EditingState | null>(null);
  const [status, setStatus] = useState<EditingStatus>('idle');
  const [busy, setBusy] = useState<boolean>(false);

  // Guards: avoid double-activation (StrictMode / re-render) and stale writes
  // after the comparison id changes or the component unmounts.
  const activatedFor = useRef<string | null>(null);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const activate = useCallback(
    async (cid: string) => {
      setBusy(true);
      setStatus('loading');
      try {
        const next = canEdit
          ? await veridocApi.enterEditing(cid)
          : await veridocApi.getEditingState(cid);
        if (!aliveRef.current) return;
        setState(next);
        setStatus('ready');
      } catch (err) {
        if (!aliveRef.current) return;
        if (err instanceof ApiError && err.code === NOT_MATERIALIZED) {
          // Viewer reached the view before any editor materialized it.
          setStatus('not-materialized');
          return;
        }
        setStatus('error');
        onError(err instanceof Error ? err.message : String(err));
      } finally {
        if (aliveRef.current) setBusy(false);
      }
    },
    [canEdit, onError],
  );

  // Single activation effect. The ref guard makes this idempotent under
  // StrictMode's double-invoke (a separate reset effect would clear the guard
  // between the two passes and fire two concurrent materialize POSTs). When
  // the comparison id genuinely changes, the guard no longer matches → we
  // reset state and re-activate for the new id.
  useEffect(() => {
    if (!enabled || !comparisonId) return;
    if (activatedFor.current === comparisonId) return;
    activatedFor.current = comparisonId;
    setState(null);
    setStatus('idle');
    void activate(comparisonId);
  }, [enabled, comparisonId, activate]);

  const runEdit = useCallback(
    async (action: (cid: string) => Promise<EditingState>) => {
      if (!comparisonId) return;
      setBusy(true);
      try {
        const next = await action(comparisonId);
        if (!aliveRef.current) return;
        setState(next);
        setStatus('ready');
      } catch (err) {
        onError(err instanceof Error ? err.message : String(err));
      } finally {
        if (aliveRef.current) setBusy(false);
      }
    },
    [comparisonId, onError],
  );

  const append = useCallback(
    (req: AppendEditRequest) =>
      runEdit((cid) => veridocApi.appendEdit(cid, req)),
    [runEdit],
  );

  const setText = useCallback(
    (nodeId: string, text: string) => {
      const runs: EditingRun[] = [{ text }];
      return append({ opType: 'set_text', targetNodeId: nodeId, payload: { runs } });
    },
    [append],
  );

  const insertNode = useCallback(
    (index: number, text: string, type = 'paragraph') =>
      append({
        opType: 'insert_node',
        payload: { index, type, runs: [{ text }] },
      }),
    [append],
  );

  const deleteNode = useCallback(
    (nodeId: string) =>
      append({ opType: 'delete_node', targetNodeId: nodeId, payload: {} }),
    [append],
  );

  const undo = useCallback(
    () =>
      runEdit(async (cid) => {
        const res = await veridocApi.undoEdit(cid);
        return res.state;
      }),
    [runEdit],
  );

  return {
    state,
    status,
    busy,
    canUndo: (state?.editOpCount ?? 0) > 0,
    setText,
    insertNode,
    deleteNode,
    undo,
  };
}
